import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Breadcrumbs from "../../component/breadcrumbs/breadcrumbs";
import { fetchArticles } from "./articlesApi";
import "./ArticleDetail.css";
import { useLanguage } from "../../i18n/LanguageContext";

const ArticleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { tr, lang } = useLanguage();
  const [article, setArticle] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth <= 768;
    }
    return false;
  });

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadArticle = async () => {
      try {
        setIsLoading(true);
        setLoadError("");
        const data = await fetchArticles();
        const foundArticle = data.find(
          (item) => String(item.id) === String(id) || item.slug === id
        );
        if (!isMounted) return;

        if (foundArticle) {
          setArticle(foundArticle);
        } else {
          navigate("/use-cases");
        }
      } catch (err) {
        console.error("Failed to load article:", err);
        if (isMounted) {
          setLoadError(
            tr(
              "Failed to load article. Please try again later.",
              "Gagal memuat artikel. Silakan coba lagi nanti."
            )
          );
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadArticle();
    return () => {
      isMounted = false;
    };
  }, [id, navigate, tr]);

  if (isLoading) {
    return (
      <div className="article-detail-page">
        <div className="article-detail-loading">{tr("Loading...", "Memuat...")}</div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="article-detail-page">
        <div className="article-detail-loading">{loadError}</div>
      </div>
    );
  }

  if (!article) {
    return null;
  }

  const getArticleTitle = (data) => tr(data.title, data.titleId || data.title);
  const getArticleDescription = (data) => tr(data.description, data.descriptionId || data.description);
  const getArticleCategory = (category) => {
    const map = {
      Business: tr("Business", "Bisnis"),
      Product: tr("Product", "Produk"),
    };
    return map[category] || category;
  };
  const formatDate = (dateStr) => {
    if (lang !== "id") return dateStr;
    const months = {
      January: "Januari",
      February: "Februari",
      March: "Maret",
      April: "April",
      May: "Mei",
      June: "Juni",
      July: "Juli",
      August: "Agustus",
      September: "September",
      October: "Oktober",
      November: "November",
      December: "Desember",
    };
    return dateStr.replace(
      /(January|February|March|April|May|June|July|August|September|October|November|December)/,
      (match) => months[match] || match
    );
  };

  return (
    <div className="article-detail-page">
      <Breadcrumbs />
      <motion.article
        className="article-detail"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: isMobile ? 0.3 : 0.6 }}
      >
        {/* Header */}
        <div className="article-detail-header">
          <div className="article-detail-image">
            <img src={article.image} alt={getArticleTitle(article)} />
          </div>
          <div className="article-detail-meta">
            <span className="article-detail-category">{getArticleCategory(article.category)}</span>
            <h1 className="article-detail-title">{getArticleTitle(article)}</h1>
            <p className="article-detail-description">{getArticleDescription(article)}</p>
            <span className="article-detail-date">{formatDate(article.date)}</span>
          </div>
        </div>

        {/* Content */}
        <div 
          className="article-detail-content"
          dangerouslySetInnerHTML={{ __html: tr(article.content, article.contentId || article.content) }}
        />

        {/* Back Button */}
        <div className="article-detail-actions">
          <button 
            className="article-detail-back-btn"
            onClick={() => navigate('/use-cases')}
          >
            {tr("← Back to Use Cases", "← Kembali ke Use Case")}
          </button>
        </div>
      </motion.article>
    </div>
  );
};

export default ArticleDetail;

